
export interface PaymentRow {
  ID: string | number;
  FinalTotal: unknown;
  SourceAccount?: unknown;
  DestinationAccount?: unknown;
  BusinessSegment?: unknown;
  OwnerName?: unknown;
  RoomName?: unknown;
  ParkingName?: unknown;
  DateFrom?: unknown;
  DateTo?: unknown;
  Status?: string;
}
export type PaymentError = { id: string; reason: string };

// Polish NRB accounts use the IBAN mod-97 check with country code PL.
const accountNumber = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const account = value.replace(/\s/g, '').replace(/^PL/i, '');
  if (!/^\d{26}$/.test(account)) return null;
  const rearranged = account.slice(2) + '2521' + account.slice(0, 2);
  let remainder = 0;
  for (const digit of rearranged) remainder = (remainder * 10 + Number(digit)) % 97;
  return remainder === 1 ? account : null;
};

const cents = (value: unknown): number | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  const result = Number(whole) * 100 + Number((fraction + '00').slice(0, 2)) +
    (Number(fraction[2] || 0) >= 5 ? 1 : 0);
  return Number.isSafeInteger(result) && result > 0 ? result : null;
};

const dateOnly = (value: unknown): string | null => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) return null;
  const date = value.slice(0, 10);
  const parsed = new Date(date + 'T00:00:00Z');
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date ? date : null;
};

const quote = (value: unknown) => '"' + String(value ?? '').replace(/[\r\n]+/g, ' ').replace(/"/g, '""') + '"';
const displayDate = (value: string) => value.split('-').reverse().join('-');

export function buildPaymentCsv(rows: PaymentRow[], selectedIds: (string | number)[], now = new Date()) {
  const errors: PaymentError[] = [];
  const lines: string[] = [];
  const date = String(now.getFullYear()) + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0');
  if (!selectedIds.length) return { csv: '', errors: [{ id: '', reason: 'selection' }] };
  for (const id of Array.from(new Set(selectedIds.map(String)))) {
    const matches = rows.filter(row => String(row.ID) === id);
    const row = matches[0];
    const reject = (reason: string) => errors.push({ id, reason });
    if (matches.length !== 1 || !/^\d+$/.test(id)) { reject('missing'); continue; }
    const source = accountNumber(row.SourceAccount);
    const destination = accountNumber(row.DestinationAccount);
    if (!source || !destination) { reject('account'); continue; }
    const amount = cents(row.FinalTotal);
    if (amount === null) { reject('amount'); continue; }
    const from = dateOnly(row.DateFrom);
    const to = dateOnly(row.DateTo);
    if (!from || !to || from > to) { reject('date'); continue; }
    if (!String(row.OwnerName ?? '').trim() || !String(row.BusinessSegment ?? '').trim()) {
      reject('name'); continue;
    }
    const title = String(row.RoomName ?? '') + '+' + String(row.ParkingName ?? '') +
      ' za okres: ' + displayDate(from) + ' - ' + displayDate(to);
    lines.push([
      '110', date, amount, source.slice(2, 10), '0', quote(source), quote(destination),
      quote(row.BusinessSegment), quote(row.OwnerName), '0', destination.slice(2, 10),
      quote(title), '""', '""', '"51"', id,
    ].join(','));
  }
  // Never silently produce a partial payment batch.
  return { csv: errors.length ? '' : lines.join('\r\n') + '\r\n', errors };
}
